import { useEffect, useRef, useState } from "react";
import { stageLabels, stages, zones, zoneLabel, resolveMode, DEMO_STEPS, useNetwork } from "./useNetwork";
import { useCarrier } from "../hooks/useCarrier";
import { useHunters } from "../hooks/useHunters";
import { useCurrentRound } from "../hooks/useCurrentRound";
import { useRoundSummary } from "../hooks/useRoundSummary";
import { reconcileTargetSelection } from "./targetSelection";
// Aliased: useNetwork's zoneLabel (above) is the simple "Zona X" used
// throughout the pre-existing UI, keyed by the full "ZONE-X" id. Ours is a
// richer venue-location description (config/zones.ts), keyed by the bare
// letter — used only in the TARGET panel's subtitle below, never for the
// general zone labels origin/main already wires everywhere else.
import { zoneLabel as zoneDescription } from "../config/zones";
import { PROXIMITY_BANDS } from "../types/api";

const CARRIER_STALE_AFTER_SECONDS = 30; // must match AIM_CARRIER_POSITION_WINDOW_SECONDS default
const time = (s: string | null) =>
  s && Number.isFinite(Date.parse(s))
    ? new Date(s).toLocaleTimeString("es-MX", { hour12: false })
    : "—";
const agoSeconds = (iso: string | null, nowMs: number) =>
  iso && Number.isFinite(Date.parse(iso)) ? `${Math.max(0, Math.floor((nowMs - Date.parse(iso)) / 1000))}s` : "—";
const elapsed = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60)
    .toString()
    .padStart(2, "0");
  const s = (total % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};
export default function AimPortal() {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("ciclope-theme") === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });
  const controlsEnabled =
    new URLSearchParams(location.search).get("controls") === "1";
  const [view, setView] = useState("Live Network"),
    [mode, setMode] = useState<"demo" | "live">(() => resolveMode(location.search)),
    // No hardcoded target default (event hardening P0): a LIVE round may
    // hold several targets at once, so the only legitimate defaults are
    // "whatever ?targetId= says" (explicit link/QR override) or "whatever
    // GET /api/v1/round/current says is first" (see the reconciliation
    // effect below) — never a fixed beacon id.
    [targetId, setTargetId] = useState(
      new URLSearchParams(location.search).get("targetId") || "",
    ),
    [draft, setDraft] = useState(targetId),
    [clock, setClock] = useState(new Date()),
    [fullscreenError, setFullscreenError] = useState("");
  // Set only by the free-text "Conectar" debug form (?controls=1): an
  // explicit operator override to inspect a target outside the current
  // round (e.g. a raw beacon not yet enabled in any round), which the
  // auto-selection effect below must never silently revert.
  const manualPin = useRef(false);
  const { data, connected, step, setStep, playing, setPlaying, totalDetections, restart, lastSync, error } =
    useNetwork(mode, targetId);
  const liveDataEnabled = mode === "live" && !!targetId;
  const { data: carrier } = useCarrier(targetId, liveDataEnabled);
  const { data: hunters } = useHunters(targetId, liveDataEnabled);
  const { data: currentRound } = useCurrentRound(mode === "live");
  const roundSummary = useRoundSummary(currentRound.targets, mode === "live" && currentRound.targets.length > 1);
  const carrierAgeSeconds = carrier.currentPosition
    ? Math.max(0, Math.floor((clock.getTime() - Date.parse(carrier.currentPosition.reportedAt)) / 1000))
    : null;
  const carrierStale = carrierAgeSeconds !== null && carrierAgeSeconds > CARRIER_STALE_AFTER_SECONDS;
  const wasRecovered = useRef(false);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("ciclope-theme", theme);
    } catch {}
  }, [theme]);
  useEffect(() => {
    document.title = "CICLOPE · AIM 2026 Portal Live";
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = data.target,
    stale = mode === "live" && !connected,
    // roundId/roundStatus are only present on `target` when the backend has
    // a round covering it (see portal_target_state — omitted entirely
    // otherwise, never sent as null). roundId ALONE is not enough to mean
    // "active": it points at the most recently relevant round for this
    // target even after that round is CLOSED (so post-recovery stats stay
    // visible right up until an operator closes it) — only roundStatus
    // tells apart "round just recovered, still showing" from "round
    // closed, nothing active". Event hardening fix: this used to read
    // !target.roundId alone, which meant the badge never appeared again
    // once a target had ever been in any round, closed or not.
    // Also true with no target selected at all (nothing chosen yet, or the
    // last round closed and nothing new started): GET /api/v1/round/current
    // reports NO_ACTIVE_ROUND independent of any single target's own state,
    // covering the case where the Portal never had a targetId to poll.
    noActiveRound =
      mode === "live" &&
      ((!!target && !stale && (!target.roundId || target.roundStatus === "CLOSED")) ||
        (!targetId && currentRound.status === "NO_ACTIVE_ROUND")),
    status =
      mode === "demo"
        ? "DEMO/REPLAY"
        : !targetId
          ? "SIN OBJETIVO ACTIVO"
          : stale
            ? "SIN CONEXIÓN"
            : !target
              ? "SIN OBJETIVO ACTIVO"
              : "LIVE",
    zone = target?.zone,
    recovery = target ? stages.indexOf(target.recoveryStatus) : -1,
    justRecovered = recovery === stages.length - 1;
  useEffect(() => {
    if (justRecovered && !wasRecovered.current && !stale) setView("Recovery View");
    if (!stale) wasRecovered.current = justRecovered;
  }, [justRecovered, stale]);
  useEffect(() => { wasRecovered.current = false; setView("Live Network"); }, [mode, targetId, target?.roundId]);
  // Target discovery/auto-selection (P2, P5): the 55" public display
  // (controlsEnabled=false) always follows the LIVE round's target set —
  // preserve the current selection while it's still in the round, else
  // jump to the first one, all without a reload. The ?controls=1 debug
  // form is exempt once an operator has manually pinned a target (useful
  // to inspect a beacon that isn't part of any round), so it never gets
  // silently reverted out from under them.
  useEffect(() => {
    if (mode !== "live") return;
    if (controlsEnabled && manualPin.current) return;
    const next = reconcileTargetSelection(currentRound, targetId);
    if (next !== targetId) setTargetId(next);
  }, [mode, controlsEnabled, currentRound, targetId]);
  const searchElapsedMs = target?.startedAt
    ? Math.max(0, (target.recoveredAt ? Date.parse(target.recoveredAt) : clock.getTime()) - Date.parse(target.startedAt))
    : null;
  const gatewaysParticipating = data.metrics.participatingGateways;
  return (
    <div className={controlsEnabled ? "aim" : "aim aim-public"}>
      <div className="aim-rail">
        <img src="/brand/ps_icono.png" alt="CICLOPE" />
        <span className="aim-rail-dot">◉</span>
        <span className="aim-vertical">INTELIGENCIA QUE RECUPERA</span>
        <b>04</b>
      </div>
      <div className="aim-body">
        <div className="aim-header">
          <div className="aim-brand">
            <img
              src={
                theme === "dark"
                  ? "/brand/ps_logotipo.png"
                  : "/brand/color_logotipo.png"
              }
              alt="CICLOPE"
            />
            <span>
              AIM INSURTECH 2026
              <br />
              <b>PORTAL LIVE</b>
            </span>
          </div>
          <div className="aim-tools">
            <a href="/reto-robo" className="reto-link"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>Reto Robo</a>
            <span className="aim-hashtag">#TodosSomosCICLOPE</span>
            <span className="aim-stand">
              STAND <b>#4</b>
            </span>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? "☀ Claro" : "◐ Oscuro"}
            </button>
            <button
              onClick={async () => {
                try {
                  if (document.fullscreenElement)
                    await document.exitFullscreen();
                  else await document.documentElement.requestFullscreen();
                  setFullscreenError("");
                } catch {
                  setFullscreenError(
                    "Pantalla completa no disponible en este navegador.",
                  );
                }
              }}
            >
              ⛶ Pantalla completa
            </button>
          </div>
        </div>
        <div className="aim-intro">
          <div>
            <p className="aim-eyebrow">RED COLABORATIVA DE LOCALIZACIÓN</p>
            <h1>Cada señal nos acerca.</h1>
            <p>
              Detectar. Localizar. Recuperar.{" "}
              <span>Inteligencia en movimiento.</span>
            </p>
          </div>
          <div className="aim-status">
            <span
              className={
                "aim-badge " +
                (stale ? "warning" : status === "LIVE" ? "live" : status === "DEMO/REPLAY" ? "demo" : "idle")
              }
            >
              {status === "LIVE" ? "●" : "◉"} {status}
            </span>
            {noActiveRound && <span className="aim-badge-sub">SIN PARTIDA ACTIVA</span>}
            <time>{clock.toLocaleTimeString("es-MX", { hour12: false })}</time>
          </div>
        </div>
        {mode === "live" && currentRound.targets.length > 1 && (
          <div className="aim-target-selector" aria-label="Objetivos de la ronda actual">
            <span>OBJETIVOS</span>
            {currentRound.targets.map((t) => (
              <button
                key={t.targetId}
                aria-pressed={targetId === t.targetId}
                onClick={() => setTargetId(t.targetId)}
              >
                {t.displayAlias || t.displayName || t.targetId}
              </button>
            ))}
          </div>
        )}
        <div className="aim-toolbar">
          <nav aria-label="Vistas del portal">
            {["Live Network", "Network View", "Recovery View"].map((v, i) => (
              <button
                key={v}
                aria-pressed={view === v}
                onClick={() => setView(v)}
              >
                <span>0{i + 1}</span> {v}
              </button>
            ))}
          </nav>
          {(
            <select
              aria-label="Fuente de datos"
              value={mode}
              onChange={(e) => setMode(e.target.value as "demo" | "live")}
            >
              <option value="demo">Demo / Replay</option>
              <option value="live">Live · API</option>
            </select>
          )}
        </div>
        {stale && (
          <p role="status" className="aim-offline-banner">
            RED CICLOPE TEMPORALMENTE SIN CONEXIÓN · {lastSync ? `Última sincronización: ${time(lastSync)}. Datos sin actualizar.` : "Esperando la primera sincronización."}
          </p>
        )}
        {mode === "live" && controlsEnabled && (
          <form
            className="aim-connect"
            onSubmit={(e) => {
              e.preventDefault();
              manualPin.current = true;
              setTargetId(draft.trim());
            }}
          >
            <label>
              Objetivo{" "}
              <input
                placeholder="ID del Beacon"
                aria-label="ID del objetivo"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </label>
            <button>Conectar</button>
            <span>
              {stale
                ? "Reconectando · los datos previos pueden estar desactualizados."
                : !targetId
                  ? "Ingresa un ID para iniciar la búsqueda."
                  : "Actualización automática · SSE"}
            </span>
          </form>
        )}
        {controlsEnabled && error && mode === "live" && <p role="status">{error}</p>}
        {fullscreenError && <p role="status">{fullscreenError}</p>}
        <div aria-live="polite" className="aim-announcement">
          {status} · {zone ? zoneLabel(zone) : "Sin zona probable"} ·{" "}
          {target ? stageLabels[target.recoveryStatus] : ""}
        </div>
        {view === "Live Network" && (
          <>
            {mode === "live" && currentRound.targets.length > 1 && (
              <section className="aim-panel aim-round-summary" aria-label="Resumen de objetivos activos">
                <div className="aim-panel-heading">
                  <div>
                    <p className="aim-eyebrow">RONDA ACTUAL</p>
                    <h2>{currentRound.targets.length} OBJETIVOS ACTIVOS</h2>
                  </div>
                </div>
                <div className="aim-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        {["Objetivo", "Portador", "Zona", "Última señal"].map((h) => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roundSummary.map((row) => (
                        <tr
                          key={row.targetId}
                          className={targetId === row.targetId ? "selected" : ""}
                          onClick={() => setTargetId(row.targetId)}
                        >
                          <td>
                            <b>{row.displayName || row.targetId}</b>
                          </td>
                          <td>{row.carrierId || "—"}</td>
                          <td>{row.zone ? zoneLabel(row.zone) : "—"}</td>
                          <td>{agoSeconds(row.lastDetection, clock.getTime())}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {/* noActiveRound (P: "FINAL UI HARDENING — NO ACTIVE ROUND"): the
                selected target is intentionally preserved internally across a
                round close (see targetSelection.ts) so its last known values
                stay available, but this block must never present them as a
                live/ongoing hunt — dimmed, relabeled "ÚLTIMO OBJETIVO", and
                every zone/confidence/detection value explicitly marked
                "última información conocida". Purely presentational: driven
                by the same noActiveRound already computed from the existing
                live-refresh signal, no new endpoint/poll. */}
            <div className={"aim-kpis" + (noActiveRound ? " aim-stale" : "")}>
              <div className="aim-target">
                <span>{noActiveRound ? "ÚLTIMO OBJETIVO" : "OBJETIVO ACTIVO"}</span>
                <strong>{target?.id || "Sin objetivo"}</strong>
                <small>
                  {mode === "demo"
                    ? "Beacon de demostración"
                    : stale
                      ? "Último dato conocido"
                      : noActiveRound
                        ? "Última partida cerrada"
                        : "Seguimiento de Beacon"}
                </small>
              </div>
              <div>
                <span>ZONA PROBABLE</span>
                <strong>{zone ? zoneLabel(zone) : "—"}</strong>
                <small>
                  {noActiveRound
                    ? zone
                      ? "Última información conocida"
                      : "Sin partida activa"
                    : zone
                      ? "Ubicación estimada por la red"
                      : "Esperando detección"}
                </small>
              </div>
              <div>
                <span>CONFIANZA</span>
                <strong>
                  {target ? target.confidence : "—"}
                  <em>{target ? "%" : ""}</em>
                </strong>
                <small>
                  {stale
                    ? "Dato sin actualizar"
                    : noActiveRound
                      ? target
                        ? "Última información conocida"
                        : "Sin partida activa"
                      : "Estimación de localización"}
                </small>
              </div>
              <div>
                <span>ÚLTIMA DETECCIÓN</span>
                <strong>{time(target?.lastDetection || null)}</strong>
                <small>
                  {target?.lastDetection
                    ? noActiveRound
                      ? "Última información conocida"
                      : `${Math.max(0, Math.floor((clock.getTime() - Date.parse(target.lastDetection)) / 1000))} s desde la última señal`
                    : "Sin lecturas disponibles"}
                </small>
              </div>
            </div>
            <div className="aim-grid">
              <section className={"aim-panel aim-map" + (noActiveRound ? " aim-stale" : "")}>
                <div className="aim-panel-heading">
                  <div>
                    <p className="aim-eyebrow">01 / LIVE NETWORK</p>
                    <h2>Una red. Seis zonas. Un objetivo.</h2>
                  </div>
                  <span className="aim-pill">
                    {mode === "demo" ? "SIMULACIÓN" : "ZONAS LÓGICAS"}
                  </span>
                </div>
                <div className="aim-floor" aria-label="Mapa de seis zonas">
                  <div className="aim-floor-caption">
                    AIM 2026 / ÁREA DE DEMOSTRACIÓN <span>N ↑</span>
                  </div>
                  <div className="aim-zones">
                    {zones.map((z) => (
                      <div
                        key={z}
                        className={"aim-zone " + (zone === z ? "selected" : "")}
                      >
                        <span>{zoneLabel(z).toUpperCase()}</span>
                        <div className="aim-zone-center">
                          {zone === z ? (
                            <div className="aim-beacon">
                              <i />
                              <b>◉</b>
                              <small>OBJETIVO</small>
                            </div>
                          ) : (
                            <span className="aim-cross">+</span>
                          )}
                        </div>
                        <div className="aim-gateway">
                          ⌁ <b>{data.observers.filter(o => o.zone === z).map(o => o.id).join(" · ") || "Sin gateway"}</b>
                          <small>
                            {data.observers.some((o) => o.zone === z && o.status === "online")
                              ? "● Online"
                              : "○ Sin señal"}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="aim-map-legend">
                    <span>
                      <i /> Zona probable
                    </span>
                    <span>⌁ Gateway</span>
                    <span>◉ Beacon</span>
                    <span>Diagrama esquemático · sin escala</span>
                  </div>
                </div>
                <div className="aim-network-metrics">
                  {[
                    [
                      "GATEWAYS ACTIVOS",
                      data.observers.filter((o) => o.status === "online")
                        .length,
                    ],
                    ["NODOS ACTIVOS", data.metrics.activeParticipants],
                    ["DETECCIONES / MIN", data.metrics.detectionsPerMinute],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <strong>{value}</strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </section>
              <section className={"aim-panel aim-recovery-mini" + (noActiveRound ? " aim-stale" : "")}>
                <p className="aim-eyebrow">02 / RECUPERACIÓN</p>
                <h2>El objetivo, cada vez más cerca.</h2>
                <div className="aim-signal">⌁</div>
                <strong className="aim-current-state">
                  {target ? stageLabels[target.recoveryStatus] : "ESPERANDO OBJETIVO"}
                </strong>
                <p>
                  {zone
                    ? noActiveRound
                      ? `Última información conocida: ${zoneLabel(zone).toLowerCase()}.`
                      : `La red sitúa el Beacon en ${zoneLabel(zone).toLowerCase()}.`
                    : "Activa un objetivo para iniciar."}
                </p>
                <div className="aim-segments">
                  {stages.map((s, i) => (
                    <i key={s} className={i <= recovery ? "on" : ""} />
                  ))}
                </div>
                <button onClick={() => setView("Recovery View")}>
                  Ver recuperación <span>↗</span>
                </button>
              </section>
            </div>
            {mode === "live" && (
              <div className="aim-grid">
                <section className={"aim-panel aim-carrier" + (noActiveRound ? " aim-stale" : "")}>
                  <div className="aim-panel-heading">
                    <div>
                      <p className="aim-eyebrow">TARGET</p>
                      <h2>Beacon en manos de Staff CICLOPE.</h2>
                    </div>
                    <span className={"aim-pill " + (noActiveRound || carrierStale ? "warning" : "")}>
                      {carrier.currentPosition
                        ? noActiveRound || carrierStale
                          ? "ÚLTIMA POSICIÓN CONOCIDA"
                          : "STAFF EN VIVO"
                        : "SIN REPORTE DE STAFF"}
                    </span>
                  </div>
                  {carrier.currentPosition ? (
                    <>
                      <div className="aim-kpis">
                        <div>
                          <span>ZONA REPORTADA</span>
                          <strong>Zona {carrier.currentPosition.zoneId.replace("ZONE-", "")}</strong>
                          <small>{zoneDescription(carrier.currentPosition.zoneId.replace("ZONE-", ""))}</small>
                        </div>
                        <div>
                          <span>PORTADOR</span>
                          <strong>{carrier.currentPosition.carrierId}</strong>
                          <small>Staff CICLOPE</small>
                        </div>
                        <div>
                          <span>ÚLTIMO REPORTE</span>
                          <strong>{carrierAgeSeconds !== null ? `hace ${carrierAgeSeconds}s` : "—"}</strong>
                          <small>{noActiveRound ? "Última información conocida" : carrierStale ? "Posición desactualizada" : "Reporte reciente"}</small>
                        </div>
                      </div>
                      {carrier.movementHistory.length > 1 && (
                        <div className="aim-carrier-trail">
                          {carrier.movementHistory
                            .slice()
                            .reverse()
                            .map((p, i) => (
                              <span key={i}>
                                {i > 0 && <i>→</i>}
                                {p.zoneId.replace("ZONE-", "")}
                              </span>
                            ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="aim-empty-state">
                      <span className="aim-empty-icon">⌁</span>
                      <b>Sin reporte de Staff</b>
                      <p>Ningún miembro de staff ha reportado posición todavía para este objetivo.</p>
                    </div>
                  )}
                </section>
                <section className="aim-panel aim-hunters">
                  <div className="aim-panel-heading">
                    <div>
                      <p className="aim-eyebrow">HUNTERS</p>
                      {/* Presence (connectedParticipants) stays event-wide and
                          normal-styled even with noActiveRound — it's true
                          regardless of round state — but the copy must not
                          claim an ongoing hunt when there's nothing to hunt. */}
                      <h2>{noActiveRound ? "#TodosSomosCICLOPE conectados." : "#TodosSomosCICLOPE en la cacería."}</h2>
                    </div>
                    <span className="aim-pill">{hunters.connectedParticipants} CONECTADOS</span>
                  </div>
                  <div className="aim-kpis">
                    <div>
                      <span>RECUPERADORES CONECTADOS</span>
                      <strong>{hunters.connectedParticipants}</strong>
                      <small>App activa · últimos 45s</small>
                    </div>
                    <div>
                      <span>CON SEÑAL</span>
                      <strong>{hunters.trackingParticipants}</strong>
                      <small>{noActiveRound ? "Sin ronda activa" : "Detectando el objetivo"}</small>
                    </div>
                    <div>
                      <span>DETECCIONES / MIN</span>
                      <strong>{hunters.detectionsPerMinute}</strong>
                      <small>Red pública</small>
                    </div>
                  </div>
                  <div className="aim-hunters-bands">
                    {PROXIMITY_BANDS.map((band) => (
                      <div key={band}>
                        <strong>{hunters.bands[band] ?? 0}</strong>
                        <span>{band}</span>
                      </div>
                    ))}
                  </div>
                  <div className="aim-hunters-list">
                    {hunters.closestParticipants.length ? (
                      hunters.closestParticipants.slice(0, 5).map((p) => (
                        <div key={p.participantId}>
                          <b>{p.participantId}</b>
                          <span>{p.band}</span>
                          <span>
                            {p.rssi} dBm
                            {p.previousRssi !== null &&
                              (p.rssi > p.previousRssi ? " ↑" : p.rssi < p.previousRssi ? " ↓" : " →")}
                          </span>
                        </div>
                      ))
                    ) : noActiveRound ? (
                      <div className="aim-empty-state">
                        <span className="aim-empty-icon">◉</span>
                        <b>Sin ronda activa</b>
                        <p>La cacería se reanuda automáticamente cuando inicie la próxima partida.</p>
                      </div>
                    ) : hunters.connectedParticipants > 0 ? (
                      <div className="aim-empty-state">
                        <span className="aim-empty-icon">◉</span>
                        <b>Hay recuperadores conectados</b>
                        <p>Aún nadie tiene señal del objetivo.</p>
                      </div>
                    ) : (
                      <div className="aim-empty-state">
                        <span className="aim-empty-icon">◉</span>
                        <b>Sin recuperadores conectados</b>
                        <p>#TodosSomosCICLOPE todavía no ha empezado a buscar — comparte el QR en /participa.</p>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}
          </>
        )}
        {view === "Network View" && (
          <section className="aim-panel">
            <p className="aim-eyebrow">02 / INFRAESTRUCTURA CONECTADA</p>
            <h2>La red detrás de cada señal.</h2>
            <div className="aim-table-wrap">
              <table>
                <thead>
                  <tr>
                    {[
                      "Gateway",
                      "Zona",
                      "RSSI",
                      "Última lectura",
                      "Heartbeat",
                      "Estado",
                    ].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.observers.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <b>⌁ {o.id}</b>
                      </td>
                      <td>{zoneLabel(o.zone)}</td>
                      <td>
                        {o.rssi ?? "—"} {o.rssi !== null ? "dBm" : ""}
                      </td>
                      <td>{time(o.lastReading)}</td>
                      <td>{time(o.lastHeartbeat ?? null)}</td>
                      <td>
                        <span
                          className={
                            "aim-pill " +
                            (o.status === "offline" ? "warning" : "")
                          }
                        >
                          {o.status === "online" ? "● Online" : "○ Offline"}
                          {stale ? " · sin actualizar" : ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data.observers.length && (
                <div className="aim-empty-state">
                  <span className="aim-empty-icon">⌁</span>
                  <b>Sin gateways disponibles</b>
                  <p>Ningún gateway ha reportado a la red todavía.</p>
                </div>
              )}
            </div>
          </section>
        )}
        {view === "Recovery View" && (
          <section
            className={
              "aim-panel aim-recovery-full" +
              (justRecovered ? " recovered" : "")
            }
          >
            <p className="aim-eyebrow">03 / RECOVERY VIEW</p>
            <div className="aim-radar">{justRecovered ? "✓" : "◉"}</div>
            <h2>
              {target ? stageLabels[target.recoveryStatus] : "SIN OBJETIVO ACTIVO"}
            </h2>
            {justRecovered ? (
              <>
                <div className="aim-recovery-stats">
                  <div>
                    <span>TIEMPO DE LA RONDA</span>
                    <strong>{searchElapsedMs === null ? "—" : elapsed(searchElapsedMs)}</strong>
                  </div>
                  <div>
                    <span>GATEWAYS PARTICIPANTES</span>
                    <strong>{gatewaysParticipating ?? "—"}</strong>
                  </div>
                  <div>
                    <span>DETECCIONES</span>
                    <strong>{totalDetections?.toLocaleString("es-MX") ?? "—"}</strong>
                  </div>
                </div>
                <p className="aim-round-note">{mode === "demo" ? "Ronda simulada · no modifica el objetivo real." : "Recuperación confirmada por el backend. — indica una métrica no proporcionada."}</p>
                <p className="aim-hashtag-banner">#TodosSomosCICLOPE</p>
              </>
            ) : (
              <p>
                {target?.id || "Esperando Beacon"} ·{" "}
                {zone ? zoneLabel(zone) : "Sin zona probable"} ·{" "}
                {target
                  ? `${target.confidence}% de confianza`
                  : "Sin detecciones"}
              </p>
            )}
            <ol>
              {stages.map((s, i) => (
                <li key={s} className={i <= recovery ? "done" : ""}>
                  <b>{i < recovery ? "✓" : `0${i + 1}`}</b>
                  <span>{stageLabels[s]}</span>
                </li>
              ))}
            </ol>
          </section>
        )}
        <section className="aim-panel aim-timeline">
          <div className="aim-panel-heading">
            <div>
              <p className="aim-eyebrow">ACTIVIDAD DE LA RED</p>
              <h2>Timeline de la red</h2>
            </div>
            <span className="aim-pill">{data.events.length} EVENTOS</span>
          </div>
          <div className="aim-events">
            {data.events.length ? (
              data.events.slice(0, controlsEnabled ? 6 : 3).map((e) => (
                <div key={e.id}>
                  <time>{time(e.timestamp)}</time>
                  <i />
                  <span>{e.message}</span>
                </div>
              ))
            ) : (
              <p>Esperando eventos de la red.</p>
            )}
          </div>
        </section>
        {mode === "demo" && (
          <div className="aim-demo">
            <div>
              <b>DEMO / REPLAY</b>
              <span>Recorrido simulado del Beacon</span>
            </div>
            <button onClick={() => setPlaying(!playing)}>
              {playing ? "Ⅱ Pausar" : "▶ Reproducir"}
            </button>
            <div className="aim-demo-zones">
              {zones.map((z, i) => (
                <button
                  aria-pressed={zone === z}
                  key={z}
                  onClick={() => {
                    setPlaying(false);
                    setStep(i + 1);
                    setView("Live Network");
                  }}
                >
                  {zoneLabel(z)}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setPlaying(false);
                setStep(7);
              }}
            >
              ✓ Recuperar
            </button>
            <button
              aria-label="Reiniciar demo"
              onClick={() => {
                restart();
                setView("Live Network");
              }}
            >
              ↺
            </button>
            <small>PASO {step + 1} / {DEMO_STEPS}</small>
          </div>
        )}
        <div className="aim-footer">
          <span>
            CICLOPE <b> / </b> INTELIGENCIA DE MOVILIDAD
          </span>
          <span>AIM 2026 · STAND #4</span>
        </div>
      </div>
    </div>
  );
}
